import React from "react";
import { RouteComponentProps } from "react-router";
import { Box, Divider, Grid, LinearProgress, Paper } from "@mui/material";
import { Typography } from "@mui/material";
import { DateTime } from "luxon";
import EventInfo from "./EventInfo";
import EventManagement from "./EventManagement";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { appContext } from "../../../../AppContext";
import IbssDatePicker from "../../../../Components/Inputs/DatePicker/IbssDatePicker";
import IbssTimePicker from "../../../../Components/Inputs/TimePicker/IbssTimePicker";
import IbssCheckBox from "../../../../Components/Inputs/CheckBox/IbssCheckBox";
import Helper, { getBuildingNameUsingFloorNodeId, getBuildingNodeIdUsingFloorNodeId, getFloorUsingFloorId } from "../../../../Common/Helper";
import { BookingParty, Bookings, ISpaceData, TicketedEvent } from "./DataModels";
import { PagedResponse } from "../../../../Providers.Api/Models";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import MapPinIcon from "../../../../Components/Icons/MapPinIcon";
import IbssButton from "../../../../Components/Buttons/Button/IbssButton";
import { Icons } from "../../../../Common/AllsvgIcons";
import IbssDrawer from "../../../../Components/Drawer/IbssDrawer";
import { IBatch, Pager } from "../../../../Common/Pager";
import SwapSpaceCard from "../../../../Components/Cards/SwapSpaceCard/SwapSpaceCard";
import IbssEventDetail from "../../../../Components/Cards/EventDetailCard/IbssEventDetail";
import MediaSelector from "../../../../Components/DialogLaunchers/MediaSelector/MediaSelector";
import { IFile } from "../../../../Providers.Api/Files/FilesRepository";
import IbssIconButton from "../../../../Components/Buttons/IconButton/IbssIconButton";
import ManageAttendees from "./ManageAttendees";
import IbssDialog from "../../../../Components/Dialogs/BaseDialog/IbssDialog";
import Spinner from "../../../../Components/Navigation/LoadingSpinner/Spinner";


class EditEvents extends IbssPage<IProps, IState>
{
    private get apiCache() { return appContext().apiCache; }
    private get local() { return appContext().localStorageProvider; }
    private get labels() { return appContext().labels; }
    private get appState() { return appContext().state; }
    
    private pager = new Pager<ISpace>();
    private isEdit = false;
    private nodeId = 0;
    private bookingId = "";

    constructor(props: IProps) 
    {
        super(props);
        this.state =
        {
            buildingNodeId: 0,
            eventId: '',
            eventOwner: '',
            eventTitle: '',
            shortDescription: '',
            webUrl: '',
            phoneNumber: '',
            fullDescription: '',
            cateringAvailable: false,
            bookingDate: DateTime.now(),
            eventDate: DateTime.now(),
            arrival: DateTime.now(),
            departure: DateTime.now(),
            thirdPartyManaged: false,
            eventCapacity: 0,
            allowWaitingList: false,
            invitePermission: '',
            havingCostToAttend: false,
            ticketPrice: 0,
            isBroadcastToIBSS: false,
            isAttendeesCheckingIn: false,
            spaceImage: '',
            spaceName: '',
            spaceCapacity: 0,
            floorName: '',
            zoneName: '',
            spaceType: '',
            spaceNodeId: 0,
            spaces: new Map(),
            pageOfSpaces: [],
            openSpaceDrawer: false,
            selectedSpaceId: '',
            selectedNodeId: 0,
            imageUrl: "",
            images: [],
            openAteeendeesDrawer: false,
            attendees: [],
            bookingPartyCount: 0,
            eventConfirmationPopup: false,
            isDraft: false,
            bookingStart: DateTime.now(),
            bookingEnd: DateTime.now(),
            isLoading: false,
            eventStatus: '',
            invitePermissionOptions:
            [
                {
                    label: this.labels.funcInternal_S,
                    value: "Internal"
                },
                {
                    label: this.labels.funcExternal_S,
                    value: "External",
                },
                {
                    label: this.labels.funcInternalAndExternal_S,
                    value: "InternalAndExternal"
                },
            ],
            linkedSpaceNames: [],
        };

        this.pager.pageSize = 10;
        this.pager.getItems = (skipToken, pageSize) => this.loadSpaces(skipToken, pageSize);
        this.isEdit = this.props.match.params.eventid ? true : false;
        this.nodeId = Number(this.props.match.params.buildingid);
        this.bookingId = this.props.match.params.bookingid;
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcEvents_S;
        const userData = this.local.getUserDetails();
        const buildingId = Number(getBuildingNodeIdUsingFloorNodeId(this.nodeId));
        this.setState
        ({
            eventOwner: userData.email,
            buildingNodeId: buildingId
        })
        if (this.isEdit)
        {
            await this.getEventDetails();
        }
        this.getImages();
        this.getSpaceDetail(buildingId);
        await this.getBookings(this.isEdit);
        await this.getBookingPartiesDetails();
    }

    private async getBookings(isEdit : boolean): Promise<void>
    {
        const getBooking = await appContext().ibssApiClientV2.v2.byNodeid.bookings.get<PagedResponse<Bookings[]>>({
            nodeId: this.nodeId,
            select: Bookings,
            filter: `Booking_Id eq ${this.bookingId}`
        });
        
        const bookingValue = getBooking.value[0]
        const cachedSpace = await this.apiCache.getSpacesByBuilding(this.nodeId)
        const linkedSpace = bookingValue.Space_Id.includes(';') ? bookingValue.Space_Id.split(';') : [];

        const spaceName = cachedSpace
        .filter(i => linkedSpace
            .includes(i.Space_Id))
            .map(i => i.Space_Name);
        if(isEdit)
        {
           await this.setStateAsync({
                bookingStart: bookingValue.Booking_Start,
                bookingEnd: bookingValue.Booking_End,
                bookingDate: bookingValue.Booking_Start,
                eventDate: bookingValue.Booking_Start,
                linkedSpaceNames: spaceName,
            });
        }
        else
        {
            await this.setStateAsync({
                bookingStart: bookingValue.Booking_Start,
                bookingEnd: bookingValue.Booking_End,
                arrival: bookingValue.Booking_Start,
                departure: bookingValue.Booking_End,
                eventDate: bookingValue.Booking_Start,
                linkedSpaceNames: spaceName,
            })
        }
       
    }

    private async getEventDetails(): Promise<void>
    {
        try 
        {
            const TicketedEventResponse = await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.get<PagedResponse<TicketedEvent[]>>
            ({
                nodeId: this.nodeId,
                select: TicketedEvent,
                filter: `Booking_Id eq ${this.bookingId}`,
                recursive: true
            });
            const ticketedEventResponseDetails = TicketedEventResponse.value[0];
            if (ticketedEventResponseDetails)
            {
                this.setState({
                    eventId: ticketedEventResponseDetails.Ticketed_Event_Id,
                    eventOwner: ticketedEventResponseDetails.Event_Owner,
                    eventTitle: ticketedEventResponseDetails.Event_Title,
                    shortDescription: ticketedEventResponseDetails.Event_Short_Description,
                    webUrl: ticketedEventResponseDetails.WebSite_URL,
                    imageUrl: ticketedEventResponseDetails.ImageURI,
                    phoneNumber: ticketedEventResponseDetails.Phone,
                    fullDescription: ticketedEventResponseDetails.Event_Description,
                    eventDate: ticketedEventResponseDetails.Event_Date,
                    arrival: ticketedEventResponseDetails.Event_Start_DateTime,
                    departure: ticketedEventResponseDetails.Event_End_DateTime,
                    thirdPartyManaged: ticketedEventResponseDetails.IsManageBy_3rdParty === 1 ? true : false,
                    eventCapacity: ticketedEventResponseDetails.Event_Capacity,
                    allowWaitingList: ticketedEventResponseDetails.IsWaitingList_Allowed === 1 ? true : false,
                    invitePermission: ticketedEventResponseDetails.Invite_Permission,
                    havingCostToAttend: ticketedEventResponseDetails.IsCost_Associated === 1 ? true : false,
                    cateringAvailable: ticketedEventResponseDetails.IsCatering_Available === 1 ? true : false,
                    ticketPrice: ticketedEventResponseDetails.Ticket_Price,
                    isBroadcastToIBSS: ticketedEventResponseDetails.IsBroadcast_ToIBSS === 1 ? true : false,
                    isAttendeesCheckingIn: ticketedEventResponseDetails.IsAttendees_CheckingIn === 1 ? true : false,
                    isDraft: ticketedEventResponseDetails.IsDraft === 1 ? true : false,
                    eventStatus: ticketedEventResponseDetails.Event_Status,
                })
            }    
        } 
        catch (error) 
        {    
        }
    }

    private async getBookingPartiesDetails(): Promise<void>
    {
        try 
        {
        
            let skipToken = '';
            const allBookingParties: BookingParty[] = [];
            do
            {
                const bookingPartiesResponse = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.get<PagedResponse<BookingParty[]>>({
                    nodeId: this.nodeId,
                    select: BookingParty,
                    top: 200,
                    filter: `Booking_Id eq ${this.bookingId} and Booking_Participant_Status eq '1' and (Booking_Participant_Type eq 1 or Booking_Participant_Type eq 2)`,
                    skipToken: skipToken,
                });

                const bookingParties = bookingPartiesResponse.value;
                skipToken = bookingPartiesResponse.skipToken || '';

                allBookingParties.push(...bookingParties);
            } while (skipToken !== '');


            this.setState({
                attendees: allBookingParties,
                bookingPartyCount: allBookingParties.filter((item) => item.IsParticipant_On_WaitingList == 0).length,
            })
        } 
        catch (error) 
        {    
        }
    }

    private async getSpaceDetail(buildingId: number): Promise<void>
    {
        const response = await this.apiCache.getSpacesByBuilding(buildingId);
        const indexedSpaces = new Map(response.map(i => [i.Space_Id, i]));
        const spaceView = response.map(space => SpaceView.fromSpace(space))
            .find(space => space.spaceId === this.props.match.params.spaceid);

        this.setState({
            spaces: indexedSpaces,
            spaceImage: spaceView?.imageUri ?? '',
            spaceName: spaceView?.spaceName ?? '',
            floorName: getFloorUsingFloorId(spaceView?.nodeId) ?? '',
            zoneName: spaceView?.zone ?? '',
            spaceType: spaceView?.spaceType ?? '',
            spaceNodeId: spaceView?.nodeId ?? 0,
            spaceCapacity: spaceView?.spaceCapacity ?? 0,
        })
    }

    private async getImages(): Promise<void>
    {
        const files = await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.get<IFile[]>({
              path: 'c/Events'
        })
        const filesWithPath = files.map((item: IFile) => {
            let newItem = { ...item }
            newItem.path = `https://storage.ibss.${Helper.environment}/images/c/Events/${item.name}`
            return newItem
        })
        this.setState({ images: filesWithPath });
    }

    private async uploadImage(filename: string, file: FormData, processAsMap: boolean): Promise<void>
    {
        await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.post({
            path: 'c/Events/' + filename,
            processAsMapFile: false,
            body: file
        });
        this.getImages();   
    }

    private handleEventOwnerChange(value: string): void
    {
        this.setState({ eventOwner: value });
    }

    private handleEventTitleChange(value: string): void
    {
        this.setState({ eventTitle: value });
    }

    private handleEventShortDescriptionChange(value: string): void
    {
        this.setState({ shortDescription: value });
    }

    private handleEventWebUrlChange(value: string): void
    {
        this.setState({ webUrl: value });
    }

    private handlePhoneNumberChange(value: string): void
    {
        this.setState({ phoneNumber: value });
    }

    private handleFullDescriptionChange(value: string): void
    {
        this.setState({ fullDescription: value });
    }

    private handleCateringAvailableChange(value: boolean): void
    {
        this.setState({ cateringAvailable: value });
    };

    private handleAllowWaitingListChange(value: boolean): void
    {
        this.setState({ allowWaitingList: value });
    };

    private handleHavingCostToAttendChange(value: boolean): void
    {
        this.setState({ havingCostToAttend: value });
    };

    private dateChanged(value: DateTime | null): void
    {
        if (value != null)
        {
            this.setState({
                eventDate: value,
                arrival: this.state.arrival.set({ year: value.year, month: value.month, day: value.day }),
                departure: this.state.departure.set({ year: value.year, month: value.month, day: value.day })
            })
        }
    }

    private async arrivalChanged(value: DateTime | null): Promise<void>
    {
        if (value != null)
        {
            await this.setStateAsync({ arrival: value });
        }
    }

    private async departureChanged(value: DateTime | null): Promise<void>
    {
        if (value != null)
        {
            await this.setStateAsync({ departure: value });
        }
    }

    public handleBroadcastToIBSSChange = (value: boolean) =>
    {
        this.setState({ isBroadcastToIBSS: value });
    }

    public handleEventCapacityChange = (value: string) =>
    {
        this.setState({ eventCapacity: Number(value) });
    }

    public handleInvitePermissionChange = (value: string) =>
    {
        this.setState({ invitePermission: value });
    }

    public handleTicketPriceChange = (value: string) =>
    {  
        this.setState({ ticketPrice: Number(value) });        
    }

    public handleThirdPartyManagedChange = (value: boolean) =>
    {
        this.setState({ thirdPartyManaged: value });
    }

    public handleIsAttendeesCheckingInChange = (value: boolean) =>
    {
        this.setState({ isAttendeesCheckingIn: value });
    }    

    public async onSubmit(isDraft = false): Promise<void>
    {
        this.setState({ isLoading: true });
        const payload = {
            Node_Id: this.nodeId,
            Booking_Id: this.bookingId,
            Event_Owner: this.state.eventOwner,
            Event_Title: this.state.eventTitle,
            Event_Short_Description: this.state.shortDescription,
            Event_Description: this.state.fullDescription,
            WebSite_URL: this.state.webUrl,
            IsCatering_Available: this.state.cateringAvailable ? 1 : 0,
            Phone: this.state.phoneNumber,
            ImageURI: this.state.imageUrl,
            Event_Date: this.state.eventDate.toUTC().startOf('day').toISO(),
            Event_Start_DateTime: this.state.arrival.setZoneByNode(this.nodeId).toUTC().toISO(),
            Event_End_DateTime: this.state.departure.setZoneByNode(this.nodeId).toUTC().toISO(),
            Event_Capacity: this.state.eventCapacity,
            IsManageBy_3rdParty: this.state.thirdPartyManaged ? 1 : 0,
            IsWaitingList_Allowed: this.state.allowWaitingList ? 1 : 0,
            IsCost_Associated: this.state.havingCostToAttend ? 1 : 0,
            Invite_Permission: this.state.invitePermission,
            IsBroadcast_ToIBSS: this.state.isBroadcastToIBSS ? 1 : 0,
            IsAttendees_CheckingIn: this.state.isAttendeesCheckingIn ? 1 : 0,
            IsDraft: isDraft ? 1 : 0,
            ...((this.state.havingCostToAttend && this.state.ticketPrice > 0) && { Ticket_Price: this.state.ticketPrice })
        };

        try
        {
            if (this.isEdit)
            {
                await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.byEventid.put({
                    nodeId: this.nodeId,
                    eventid: this.state.eventId,
                    body: payload,
                });
            } 
            else if (isDraft)
            {
                await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.post({
                    nodeId: this.nodeId,
                    body: { ...payload, IsDraft: 1 }, 
                });
                this.props.history.goBack();
            } 
            else
            {
                await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.post({
                    nodeId: this.nodeId,
                    body: payload,
                });
            }

            if (!isDraft)
            {
                this.setState({ eventConfirmationPopup: true });
            }
        } 
        catch (error)
        {
            this.setState({ isLoading: false });  
        }
        finally 
        {
            this.setState({ isLoading: false });
        }
    }
    

    private async deleteEvent(): Promise<void>
    {
        this.setState({ isLoading: true });
        try 
        {
            const data = await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.byEventid.delete
            ({
              nodeId: this.nodeId,
              eventid: this.state.eventId
            })  
            if(data)
            {
                this.setState({ isLoading: false });
                this.props.history.goBack();
            }
        } 
        catch (error) 
        { 
            this.setState({ isLoading: false });
        }
    }

    private async cancelEvent(): Promise<void>
    {
        this.props.history.goBack();  
    }

    private get payload(): ISearchRecurring
    {
        const payload: ISearchRecurring = {
            Booking_Dates: [{
                Start_Time: this.state.bookingStart.toUtcByNode(this.nodeId).toISO(),
                End_Time: this.state.bookingEnd.toUtcByNode(this.nodeId).toISO(),
            }],
            Floor_Id: this.nodeId,
            Space_Type: this.state.spaceType,
            Space_Capacity: this.state.spaceCapacity,
        };
        return payload;
    }

    private async handlePreviousPageClick(): Promise<void>
    {
        return this.changePage(-1);
    }

    private async handleNextPageClick(): Promise<void>
    {
        return this.changePage(+1);
    }

    private async changePage(offset: number): Promise<void>
    {
        this.pager.pageIndex += offset;
        const pageOfSpaces = await this.pager.getCurrentPage();
        this.setState({ pageOfSpaces: pageOfSpaces });
    }

    private async loadSpaces(skipToken: string | null, pageSize: number): Promise<IBatch<ISpace>>
    {
        try
        {
            const { spaces } = this.state;

            const spacesData = await appContext().ibssApiClientV2.v2.byNodeid.spaces.search.post<PagedResponse<ISpaceData[]>>({
                nodeId: this.nodeId,
                top: pageSize,
                skipToken: skipToken ?? undefined,
                body: this.payload,
            });

            const spacesDetails = spacesData.value.map(i =>
            {
                const space = spaces.get(i.Space_Id) ?? null;
                return {
                    id: i.Space_Id,
                    nodeId: space?.Node_Id ?? 0,
                    name: space?.Space_Name ?? '',
                    bookingPolicyId: space?.Booking_Policy_Id ?? '',
                    imageURI: space?.ImageURI ?? '',
                    spaceType: space?.Space_Type ?? '',
                    capacity: space?.Space_Capacity ?? 0,
                    hasCatering: space?.Meta_Serv_Reqs_Catering == 1,
                    hasHearing: space?.Meta_Serv_Reqs_Hearing == 1,
                    hasAv: space?.Meta_Serv_Reqs_AV == 1,
                    hasPresentationAids: space?.Meta_Serv_Reqs_Presentation == 1,
                    zone: space?.Meta_Loc_Zone ?? '',

                } as ISpace;
            });

            return { skipToken: spacesData.skipToken, items: spacesDetails };
        }
        finally
        {
        }
    }

    public handleSpaceSelect(spaceId: string, nodeId: number): void
    {
        this.setState({ selectedSpaceId: spaceId, selectedNodeId: nodeId });
    }

    public async openDrawer(): Promise<void>
    {
        this.setState({ openSpaceDrawer: true });
        const pageOfSpaces = await this.pager.getCurrentPage();
        this.setState({
            pageOfSpaces: pageOfSpaces,
        });

    }

    public navigateToAnnouncement(): void
    {
        this.props.history.push(`/oneLens/${this.state.buildingNodeId}/announcements/create`);
    }

    public navigateToAllEvents(): void
    {
        this.props.history.push(`/flex/events/mine`);
    }

    public async swapSpace(): Promise<void>
    {
        this.setState({isLoading: true});
        await appContext().ibssApiClientV2.v2.byNodeid.bookings.byBookingid.put({
            nodeId: this.state.selectedNodeId,
            bookingid: this.bookingId,
            body: {
                Space_Id: this.state.selectedSpaceId,
            }
        })
        this.setState({ 
            isLoading: false,
            openSpaceDrawer: false 
        });
        this.props.history.push(`/flex-my-bookings/${this.state.buildingNodeId}/mybooking/${this.bookingId}/${this.state.selectedSpaceId}`)
    }

    public requiredFieldsAreValid(): boolean 
    {
        return (
          this.state.invitePermission.length > 0 &&
          this.state.eventTitle.length > 0 &&
          this.state.shortDescription.length > 0 &&
          this.state.fullDescription.length > 0 &&
          this.state.phoneNumber.length > 0 &&
          this.state.imageUrl.length > 0 &&
          (!this.state.havingCostToAttend ||  this.state.ticketPrice > 0)
        );
    }

    public render(): JSX.Element
    {

        const pagingButtonStyle = (isDisabled: boolean) => ({
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '2rem',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            margin: '0 5px',
            color: isDisabled ? 'var(--ui-mid-tone)' : 'var(--ui-text-light)',
        });

        const fieldsAreValid = this.requiredFieldsAreValid();
        const submitButtonDisable = (this.state.eventStatus === "Cancelled" || this.state.eventStatus === "Completed" || this.state.eventStatus === "Completed_Booked");
        
        return (
            <Box className="page-height-exct-header">
                <Box className="rightPanel-main-content">
                    <Grid container>
                        <IbssDialog
                            aria-modal="true"
                            aria-label="change booking details warning modal"
                            fullWidth
                            maxWidth={'sm'}
                            open={this.state.eventConfirmationPopup}
                            onClose={() => this.setState({ eventConfirmationPopup: false })}
                            dialogContent=
                            {
                                <Box p={2}>
                                    <Box my={2}>
                                        <Typography variant="h5">
                                            {this.isEdit ? this.labels.funcEventUpdated_S  : this.labels.funcEventCreated_S}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" className="text-secondary">
                                        {this.labels.funcAnnouncementForYourEvent_S}
                                    </Typography>
                                    <Box display={'flex'} flexDirection={"column"} gap={2} my={2}>
                                        <IbssButton fullWidth variant="contained" color="primary" onClick={() => this.navigateToAnnouncement()}>
                                            {this.labels.funcCreateAnAnnouncement_L}
                                        </IbssButton>
                                        <IbssButton fullWidth variant="contained" color="primary" onClick={() => this.navigateToAllEvents()}>
                                            {this.labels.funcNoSeeAllEvents_L}
                                        </IbssButton>
                                    </Box>
                                </Box>
                            }
                        />
                        {/* Left Column */}
                        <Grid item xs={12} md={8}>
                            {this.state.isLoading && <Spinner />}
                            <Paper sx={{ p: 2, mr: 2 }}>
                                <Typography variant="h5" marginBottom={2}>
                                    {this.isEdit ? this.labels.OneLens_EventList_EditEventTitle : this.labels.OneLens_EventList_CreateEventTitle}
                                </Typography>
                                <Typography variant="subtitle1" marginBottom={2}>
                                    {this.labels.funcFillDetailsBelow_L}
                                </Typography>
                                <Divider />
                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <EventInfo
                                        eventOwner={this.state.eventOwner}
                                        eventOwnerChange={(value) => this.handleEventOwnerChange(value)}
                                        eventTitle={this.state.eventTitle}
                                        eventTitleChange={(value) => this.handleEventTitleChange(value)}
                                        shortDescription={this.state.shortDescription}
                                        eventShortDescriptionChange={(value) => this.handleEventShortDescriptionChange(value)}
                                        webUrl={this.state.webUrl}
                                        eventWebUrlChange={(value) => this.handleEventWebUrlChange(value)}
                                        phoneNumber={this.state.phoneNumber}
                                        phoneNumberChange={(value) => this.handlePhoneNumberChange(value)}
                                        fullDescription={this.state.fullDescription}
                                        fullDescriptionChange={(value) => this.handleFullDescriptionChange(value)}
                                        cateringAvailable={this.state.cateringAvailable}
                                        crossBuildingFilterChange={(value) => this.handleCateringAvailableChange(value)}
                                    />
                                    <Grid item xs={12}>
                                        <MediaSelector
                                            options={this.state.images.map(x => { return { name: x.name, url: x.path } })}
                                            imageSelected={(file: { name: string, url: string }) => this.setState({ imageUrl: file.url })}
                                            uploadFile={(filename: string, file: FormData, processAsMap: boolean) => this.uploadImage(filename, file, processAsMap)}
                                            defaultSelectedImage={this.state.imageUrl}
                                            required
                                        />
                                        <Divider />
                                        <Typography variant="h6" marginBottom={2}>
                                            {this.labels.funcEventDetails_L}
                                        </Typography>
                                    </Grid>
                                    <Divider />
                                    <Grid item xs={12}>
                                        <IbssDatePicker
                                            value={this.state.bookingStart}
                                            disabled={true}
                                            onChange={(e) => this.dateChanged(e)}
                                            slotProps={{ textField: { label: this.labels.HubLabelDate, fullWidth: true } }}
                                            className='mb-1'
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                    <Box p={2} mb={2} sx={{ backgroundColor: this.appState.lightModeTheme ? 'var(--ui-warn-pastel)' : 'var(--ui-graph3)', borderRadius: 2 }}>
                                        <Typography variant="body1">{this.labels.funcBookingEventTime_D} {this.state.bookingStart.toLocaleTimeString()} and {this.state.bookingEnd.toLocaleTimeString()}</Typography>
                                    </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <IbssTimePicker
                                            label={this.labels.HubLabelArrival}
                                            value={this.state.arrival}
                                            onChange={e => this.arrivalChanged(e)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                            minTime={this.state.bookingStart}
                                            maxTime={this.state.bookingEnd}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <IbssTimePicker
                                            label={this.labels.HubLabelDeparture}
                                            value={this.state.departure}
                                            onChange={e => this.departureChanged(e)}
                                            slotProps={{ textField: { fullWidth: true } }}
                                            minTime={this.state.bookingStart}
                                            maxTime={this.state.bookingEnd}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <IbssCheckBox
                                            label={this.labels.funcThirdPartyManaged_L}
                                            checked={this.state.thirdPartyManaged}
                                            onClicked={(event: React.ChangeEvent<HTMLInputElement>) => this.handleThirdPartyManagedChange(event.target.checked)}
                                        />
                                        <Divider />
                                    </Grid>
                                    <EventManagement
                                        isEditMode={this.isEdit}  
                                        eventCapacity={this.state.eventCapacity}
                                        eventCapacityChange={(value) => this.handleEventCapacityChange(value)}
                                        allowWaitingList={this.state.allowWaitingList}
                                        allowWaitingListChange={(value) => this.handleAllowWaitingListChange(value)}
                                        invitePermission={this.state.invitePermission}
                                        invitePermissionChange={(value) => this.handleInvitePermissionChange(value)}
                                        havingCostToAttend={this.state.havingCostToAttend}
                                        havingCostToAttendChange={(value) => this.handleHavingCostToAttendChange(value)}
                                        ticketPrice={this.state.ticketPrice}
                                        ticketPriceChange={(value) => this.handleTicketPriceChange(value)}
                                        isBroadcastToIBSS={this.state.isBroadcastToIBSS}
                                        broadcastToIBSSChange={(value) => this.handleBroadcastToIBSSChange(value)}
                                        isAttendeesCheckingIn={this.state.isAttendeesCheckingIn}
                                        isAttendeesCheckingInChange={(value) => this.handleIsAttendeesCheckingInChange(value)}
                                        invitePermissionOptions={this.state.invitePermissionOptions}
                                    />
                                </Grid>
                            </Paper>
                        </Grid>

                        <IbssDrawer
                            open={this.state.openSpaceDrawer}
                            onClose={() => this.setState({ openSpaceDrawer: false })}
                            anchor='right'
                            className='flex-search-filter-criteria'
                            sx={{ backgroundColor: "var(--ui-background-alternate)" }}
                        >
                            {this.state.pageOfSpaces.length > 0 ?
                            <Box p={4}>
                                <Typography variant="h6" marginBottom={2}>{this.labels.funcSelectSpace_S}</Typography>
                                {this.state.pageOfSpaces.map((spaces, index) => (
                                    <SwapSpaceCard
                                        key={spaces.id}
                                        space={spaces}
                                        floorName={getFloorUsingFloorId(spaces.nodeId)}
                                        selectedSpaceId={this.state.selectedSpaceId}
                                        handleSpaceSelect={(id) => this.handleSpaceSelect(id, spaces.nodeId)}
                                    />
                                ))}
                                <Box display={'flex'} justifyContent={'end'}>
                                    <button
                                        disabled={this.pager.isFirstPage}
                                        onClick={() => this.handlePreviousPageClick()}
                                        title={this.labels.HubButtonPrevious}
                                        style={pagingButtonStyle(this.pager.isFirstPage)}
                                    >
                                        &lt;
                                    </button>
                                    <button
                                        disabled={this.pager.isLastPage}
                                        onClick={() => this.handleNextPageClick()}
                                        title={this.labels.HubButtonNext}
                                        style={pagingButtonStyle(this.pager.isLastPage)}
                                    >
                                        &gt;
                                    </button>
                                    </Box>
                                    <Box mt={2}>
                                    <IbssButton variant='contained' color='primary' fullWidth onClick={() => this.swapSpace()}>{this.labels.funcSelectSpace_S}</IbssButton>
                                </Box>
                            </Box> : 
                            <Box p={4}>
                                <Typography variant="h6" marginBottom={2}>{this.labels.funcScheduleNoSpaces_L}</Typography>
                            </Box>
                            }
                        </IbssDrawer>

                        <ManageAttendees
                            open={this.state.openAteeendeesDrawer}
                            onClose={() => this.setState({ openAteeendeesDrawer: false })}
                            nodeId={this.nodeId}
                            bookingId={this.bookingId}
                            eventId={this.state.eventId}
                            attendees={this.state.attendees}
                            eventCapacity={this.state.eventCapacity}
                            allowWaitingList={this.state.allowWaitingList}
                            bookingPartyCount={this.state.bookingPartyCount}
                            getBookingPartiesDetails={() => this.getBookingPartiesDetails()}
                            invitePermission={this.state.invitePermission}
                        />

                        {/* Right Column */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3 }}>
                                <IbssEventDetail
                                    spaceType={this.state.spaceType}
                                    spaceImage={this.state.spaceImage}
                                    spaceName={this.state.spaceName}
                                    floorName={this.state.floorName}
                                    zoneName={this.state.zoneName}
                                    linkedSpaceNames={this.state.linkedSpaceNames}
                                />
                                {this.isEdit &&
                                    <Paper>
                                        <IbssButton onClick={() => this.openDrawer()} sx={{mt: 2, px: 2,width: '100%', justifyContent: 'space-between', display: 'flex', fontWeight: 'normal', py: 2,color:theme => theme.palette.text.secondary, borderColor: 'var(--ui-line-break)'}}>
                                            <Typography variant="body2" display={'flex'} alignItems={'center'} textTransform={'none'} fontWeight={'normal'} gap={1}>
                                                <IbssSvgIcon sx={{ fontSize: 14, pr: 1 }}>
                                                    <MapPinIcon />
                                                </IbssSvgIcon>
                                                {this.labels.funcEditLocationSpace_S}

                                            </Typography>
                                            <IbssSvgIcon fontSize='medium' className="" sx={{ color: (theme) => theme.palette.text.primary }}>
                                                {Icons.RightArrowIcon}
                                            </IbssSvgIcon>
                                        </IbssButton>
                                    </Paper>
                                }
                                {this.isEdit && 
                                <Paper sx={{ mt: 2, p: 2 }}>
                                    <Typography variant="body2" display={'flex'} alignItems={'center'} textTransform={'none'} fontWeight={'normal'}>{this.labels.funcAttendance_S}</Typography>
                                    <Box my={2} display={'flex'} justifyContent={'space-between'} gap={2}>
                                        <Box width={'100%'}>
                                            <LinearProgress variant="determinate" value={Math.min((this.state.bookingPartyCount / this.state.eventCapacity) * 100, 100)} sx={{ mt: 1 }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" noWrap>{this.state.bookingPartyCount} / {this.state.eventCapacity}</Typography>
                                        </Box>
                                    </Box>
                                    <Box >
                                        <IbssButton fullWidth sx={{ mt: 2 }} onClick={() => this.setState({ openAteeendeesDrawer: true })}>{this.labels.funcManageAttendees_L}</IbssButton>
                                    </Box>
                                </Paper>
                                }
                                {this.isEdit &&
                                    <Paper>
                                        <IbssButton onClick={() => this.props.history.push(`/flex/events/${this.state.eventId}/${this.state.spaceNodeId}`)} sx={{mt: 2, px: 2, justifyContent: 'space-between',width: '100%', display: 'flex', fontWeight: 'normal', py: 2,color:theme => theme.palette.text.secondary, BorderColor: 'var(--ui-line-break)' }}>
                                            <Typography variant="body2" display={'flex'} alignItems={'center'} textTransform={'none'} fontWeight={'normal'}>
                                                {this.labels.funcViewEventPage_L}
                                            </Typography>
                                            <IbssSvgIcon fontSize='medium' className="" sx={{ color: (theme) => theme.palette.text.primary }}>
                                                {Icons.RightArrowIcon}
                                            </IbssSvgIcon>
                                        </IbssButton>
                                    </Paper>
                                }
                                <Box>
                                    <IbssButton variant="contained" color="primary" disabled={submitButtonDisable || !fieldsAreValid} fullWidth sx={{ mt: 2 }} onClick={() => this.onSubmit()}>{this.isEdit ? this.labels.funcSaveAndUpdate_S : this.labels.funcCreateAndPublish_S}</IbssButton>
                                    {this.isEdit ?
                                        <IbssButton variant="contained" color="error" disabled={submitButtonDisable} fullWidth sx={{ mt: 2 }} onClick={() => this.deleteEvent()}>{this.labels.funcDeleteEvent_S}</IbssButton>
                                        : <IbssButton variant="contained" color="secondary" disabled={this.state.isDraft || !fieldsAreValid} fullWidth sx={{ mt: 2 }} onClick={() => this.onSubmit(true)}>
                                            {this.labels.funcAnnouncementSaveAsDraft_S}
                                        </IbssButton>
                                    }
                                    {!submitButtonDisable &&
                                        <Box mt={2} display={'flex'} justifyContent={'center'}>
                                            <IbssButton fullWidth onClick={() => this.cancelEvent()}>{this.labels.HubLabelCancelChanges}</IbssButton>
                                        </Box>
                                    }
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }
}

interface IProps extends RouteComponentProps<IQueryParams>
{
}


export interface IQueryParams
{
    buildingid: string;
    bookingid: string;
    spaceid: string;
    eventid: string;
}
interface IState
{
    buildingNodeId: number;
    eventId: string;
    eventOwner: string;
    eventTitle: string;
    shortDescription: string;
    webUrl: string;
    phoneNumber: string;
    fullDescription: string;
    cateringAvailable: boolean;
    bookingDate: DateTime;
    eventDate: DateTime;
    arrival: DateTime;
    departure: DateTime;
    thirdPartyManaged: boolean;
    eventCapacity: number;
    allowWaitingList: boolean;
    invitePermission: string;
    havingCostToAttend: boolean;
    ticketPrice: number;
    isBroadcastToIBSS: boolean;
    isAttendeesCheckingIn: boolean;
    spaceImage: string;
    spaceName: string;
    spaceCapacity: number;
    floorName: string;
    zoneName: string;
    openSpaceDrawer: boolean;
    spaces: Map<string, Space>;
    pageOfSpaces: ISpace[];
    selectedSpaceId: string;
    selectedNodeId: number;
    images: IFile[];
    imageUrl: string;
    spaceType: string;
    openAteeendeesDrawer: boolean;
    attendees: BookingParty[],
    bookingPartyCount: number;
    eventConfirmationPopup: boolean;
    isDraft: boolean;
    bookingStart: DateTime;
    bookingEnd: DateTime;
    isLoading: boolean;
    eventStatus: string;
    invitePermissionOptions: IFilterOptions[];
    linkedSpaceNames: string[];
    spaceNodeId: number;
}

export interface IFilterOptions
{
    label: string;
    value: string;
}

export interface IBookingDates
{
    Start_Time: string;
    End_Time: string;
}

export interface ISearchRecurring
{
    Booking_Dates: IBookingDates[] | null;
    Floor_Id: number | null;
    Space_Type: string | null;
    Space_Capacity: number;
}

export interface ISpace
{
    id: string;
    nodeId: number;
    name: string;
    bookingPolicyId: string,
    imageURI: string;
    capacity: number;
    hasCatering: boolean;
    spaceType: string;
    hasHearing: boolean;
    hasAv: boolean;
    hasPresentationAids: boolean;
    zone: string;
}


export class Space
{
    public Node_Id = 0;
    public Space_Id = "";
    public Space_Name = "";
    public Meta_Loc_Zone = "";
    public ImageURI = "";
    public Space_Type = "";
    public Space_Capacity = 0;
    public Booking_Policy_Id = "";
    public Meta_Serv_Reqs_Catering = 0;
    public Meta_Serv_Reqs_Hearing = 0;
    public Meta_Serv_Reqs_AV = 0;
    public Meta_Serv_Reqs_Presentation = 0;
}

class SpaceView
{
    public nodeId = 0;
    public spaceId = "";
    public spaceName = "";;
    public zone = "";
    public imageUri = "";
    public spaceType = "";
    public spaceCapacity = 0;



    public static fromSpace(space: Space): SpaceView
    {
        return {
            nodeId: space.Node_Id,
            spaceId: space.Space_Id,
            spaceName: space.Space_Name,
            zone: space.Meta_Loc_Zone,
            imageUri: space.ImageURI,
            spaceType: space.Space_Type,
            spaceCapacity: space.Space_Capacity,
        }
    }
}


export default EditEvents;